import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Input, Modal, Spin, type InputRef } from "antd";
import {
  SearchOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  AccountBookOutlined,
  BgColorsOutlined,
  UserOutlined,
  StarOutlined,
  ArrowRightOutlined,
  WalletOutlined,
  SoundOutlined,
} from "@ant-design/icons";
import { supabaseClient } from "../providers/supabase-client";
import { useEdel } from "../contexts/color-mode";
import { visibleSections } from "./nav";
import { useLabs } from "./flags";
import { STAGE_BY_KEY, type StageKey } from "../features/journey/stages";
import { setPaletteOpen, togglePalette, usePaletteOpen } from "./palette-store";
import { openPaymentWizard } from "../pages/finans/payment-store";

/** Türkçe duyarlı, aksansız karşılaştırma anahtarı */
const norm = (s: string) =>
  s
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i");

/** PostgREST or() sözdizimini bozan karakterleri at */
const sanitize = (s: string) => s.replace(/[,()%*\\:"']/g, " ").trim();

interface Row {
  key: string;
  group: "Kişiler" | "Hızlı işlemler" | "Git";
  title: string;
  sub?: string;
  icon: ReactNode;
  run: () => void;
  haystack: string;
}

interface PersonHit {
  kind: "lead" | "student" | "teacher";
  id: string;
  name: string;
  sub: string;
}

const KIND_LABEL: Record<PersonHit["kind"], string> = {
  lead: "Kişi",
  student: "Kişi · uygulama",
  teacher: "Eğitmen",
};

function usePeopleSearch(query: string, enabled: boolean) {
  const [hits, setHits] = useState<PersonHit[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const q = sanitize(query);
    if (!enabled || q.length < 2) {
      setHits([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = window.setTimeout(async () => {
      const digits = q.replace(/\D/g, "").replace(/^0+/, "");
      const phone = digits.length >= 3 ? [`phone.ilike.%${digits}%`] : [];
      const [people, teachers] = await Promise.all([
        supabaseClient
          .from("crm_journey")
          .select("lead_id,name,phone,email,stage,profile_id")
          .or([`name.ilike.%${q}%`, `email.ilike.%${q}%`, ...phone].join(","))
          .limit(8),
        supabaseClient
          .from("profiles")
          .select("id,full_name,phone,email")
          .eq("role", "teacher")
          .is("deleted_at", null)
          .or([`full_name.ilike.%${q}%`, `email.ilike.%${q}%`, ...phone].join(","))
          .limit(4),
      ]);
      if (cancelled) return;
      const out: PersonHit[] = [];
      for (const p of people.data ?? []) {
        out.push({
          kind: p.profile_id ? "student" : "lead",
          id: p.lead_id,
          name: p.name || p.email || "İsimsiz",
          sub: [STAGE_BY_KEY[p.stage as StageKey]?.label, p.phone, p.email].filter(Boolean).join(" · "),
        });
      }
      for (const t of teachers.data ?? []) {
        out.push({
          kind: "teacher",
          id: t.id,
          name: t.full_name || t.email || "İsimsiz",
          sub: [t.phone, t.email].filter(Boolean).join(" · "),
        });
      }
      setHits(out);
      setLoading(false);
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query, enabled]);
  return { hits, loading };
}

/** ⌘K / Ctrl+K: "Ne yapmak istiyorsun?" — kişi bul, işlem başlat, sayfaya git. */
export const CommandPalette = () => {
  const open = usePaletteOpen();
  const navigate = useNavigate();
  const { setMode } = useEdel();
  const [labs] = useLabs();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<InputRef>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const { hits, loading } = usePeopleSearch(query, open);

  // Kısayol
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (e.repeat) return; // basılı tutulan tuş paleti aç/kapa yapmasın
        togglePalette();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const close = () => setPaletteOpen(false);
  const go = (path: string) => {
    close();
    navigate(path);
  };

  const rows = useMemo<Row[]>(() => {
    const people: Row[] = hits.map((h) => ({
      key: `${h.kind}-${h.id}`,
      group: "Kişiler",
      title: h.name,
      sub: `${KIND_LABEL[h.kind]}${h.sub ? ` · ${h.sub}` : ""}`,
      icon: h.kind === "teacher" ? <StarOutlined /> : <UserOutlined />,
      run: () => go(h.kind === "teacher" ? `/egitmenler?ac=${h.id}` : `/kisiler/${h.id}`),
      haystack: "",
    }));

    const actions: Row[] = [
      { key: "a-lead", title: "Kişi ekle", sub: "Telefon ya da formdan gelen yeni kişi", icon: <UserAddOutlined />, run: () => go("/kisiler?yeni=1") },
      { key: "a-user", title: "Yeni öğrenci ya da eğitmen hesabı aç", sub: "Uygulamaya giriş yapabilen hesap", icon: <UsergroupAddOutlined />, run: () => go("/profiles?yeni=1") },
      { key: "a-appt", title: "Ders ya da randevu planla", icon: <CalendarOutlined />, run: () => go("/appointments") },
      { key: "a-task", title: "Görev ekle", sub: "Ekipten birine iş ver", icon: <CheckSquareOutlined />, run: () => go("/gorevler?yeni=1") },
      { key: "a-pay", title: "Ödeme al", sub: "Satış, taksit ya da tahsilat kaydet", icon: <WalletOutlined />, run: () => { close(); openPaymentWizard(); } },
      { key: "a-fin", title: "Gider ekle", icon: <AccountBookOutlined />, run: () => go("/finans/gider?yeni=1") },
      { key: "a-ann", title: "Duyuru gönder", sub: "Öğrencilere ya da eğitmenlere uygulama bildirimi", icon: <SoundOutlined />, run: () => go("/announcements?yeni=1") },
      { key: "a-theme", title: "Temayı değiştir (açık / koyu)", icon: <BgColorsOutlined />, run: () => { close(); setMode(); } },
    ].map((a) => ({ ...a, group: "Hızlı işlemler" as const, haystack: norm(`${a.title} ${a.sub ?? ""}`) }));

    const nav: Row[] = visibleSections(labs).flatMap((s) =>
      s.tabs.map((t) => {
        const Icon = s.icon;
        const title = s.tabs.length > 1 ? `${s.label} › ${t.label}` : s.label;
        return {
          key: `n-${t.path}`,
          group: "Git" as const,
          title,
          icon: <Icon />,
          run: () => go(t.path),
          haystack: norm(`${title} ${(s.keywords ?? []).join(" ")}`),
        };
      })
    );

    const q = norm(query.trim());
    const match = (r: Row) => !q || q.split(/\s+/).every((w) => r.haystack.includes(w));
    return [
      ...people,
      ...actions.filter(match),
      ...(q ? nav.filter(match) : nav.slice(0, 8)),
    ];
    // go/close sabit davranışlı; hits/query/labs değişince yeniden kurulur
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hits, query, labs]);

  useEffect(() => setActive(0), [query, hits]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-idx="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      rows[active]?.run();
    }
  };

  let lastGroup = "";
  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      closable={false}
      width={600}
      style={{ top: 80 }}
      destroyOnHidden
      afterOpenChange={(o) => o && inputRef.current?.focus()}
      title={null}
    >
      <Input
        ref={inputRef}
        size="large"
        className="es-palette__input"
        prefix={<SearchOutlined style={{ color: "var(--edel-text-soft)" }} />}
        suffix={loading ? <Spin size="small" /> : null}
        placeholder="Ne yapmak istiyorsun? Kişi ara ya da işlem yaz…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        aria-label="Komut ya da kişi ara"
        role="combobox"
        aria-expanded
        aria-controls="es-palette-list"
        aria-activedescendant={rows[active] ? `es-pal-${rows[active].key}` : undefined}
      />
      <ul className="es-palette__list" id="es-palette-list" role="listbox" ref={listRef}>
        {rows.length === 0 && !loading && (
          <li className="es-palette__empty">
            "{query}" için sonuç yok. İsmin bir kısmını, telefonun son hanelerini ya da bir işlem adı dene.
          </li>
        )}
        {rows.map((r, i) => {
          const header = r.group !== lastGroup ? r.group : null;
          lastGroup = r.group;
          return (
            <li key={r.key}>
              {header && <div className="es-palette__group">{header}</div>}
              <button
                type="button"
                id={`es-pal-${r.key}`}
                data-idx={i}
                role="option"
                aria-selected={i === active}
                className="es-palette__item"
                onMouseEnter={() => setActive(i)}
                onClick={() => r.run()}
              >
                <span className="es-palette__icon" aria-hidden>{r.icon}</span>
                <span className="es-palette__main">
                  <span className="es-palette__title">{r.title}</span>
                  {r.sub && <span className="es-palette__sub">{r.sub}</span>}
                </span>
                {i === active && <ArrowRightOutlined style={{ color: "var(--edel-text-soft)" }} />}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="es-palette__hint">
        <span><span className="es-kbd">↑</span> <span className="es-kbd">↓</span> seç</span>
        <span><span className="es-kbd">Enter</span> aç</span>
        <span><span className="es-kbd">Esc</span> kapat</span>
      </div>
    </Modal>
  );
};
